import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { OfflineBar } from "./components/OfflineBar";
import "./styles/global.css";
import "highlight.js/styles/github-dark.css";

// Lock orientation to portrait on mobile
function lockPortrait() {
  try {
    (screen.orientation as unknown as { lock?: (o: string) => Promise<void> })?.lock?.("portrait").catch(() => {});
  } catch {}
}

// Check if running as standalone PWA
const isStandalone = window.matchMedia("(display-mode: standalone)").matches
  || (navigator as unknown as { standalone?: boolean }).standalone === true;

// In PWA mode: lock works directly
if (isStandalone) {
  lockPortrait();
}

// In browser mode: use fullscreen trick on first interaction
// Enter fullscreen briefly → lock orientation → exit fullscreen
function fullscreenOrientationLock() {
  // Only on mobile (touch devices with small screens)
  if (!('ontouchstart' in window) || window.innerWidth > 1024) return;
  // Already locked?
  try {
    if (screen.orientation?.type?.startsWith("portrait")) {
      lockPortrait();
      return;
    }
  } catch {}

  const el = document.documentElement;
  const requestFs = el.requestFullscreen
    ?? (el as unknown as { webkitRequestFullscreen?: () => Promise<void> }).webkitRequestFullscreen;

  if (!requestFs) return;

  requestFs.call(el)
    .then(() => {
      lockPortrait();
      // Brief delay to let the lock take effect, then exit fullscreen
      setTimeout(() => {
        (document.exitFullscreen
          ?? (document as unknown as { webkitExitFullscreen?: () => Promise<void> }).webkitExitFullscreen
        )?.call(document);
      }, 300);
    })
    .catch(() => {
      // Fullscreen denied — can't lock orientation in browser mode
    });
}

// Try lock after first user interaction
function onFirstInteraction() {
  if (isStandalone) {
    lockPortrait();
  } else {
    fullscreenOrientationLock();
  }
}
document.addEventListener("click", onFirstInteraction, { once: true });
document.addEventListener("touchstart", onFirstInteraction, { once: true });

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ErrorBoundary>
      <OfflineBar />
      <App />
    </ErrorBoundary>
  </StrictMode>,
);
